import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Brain,
  CheckCircle2,
  ClipboardCheck,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Loader2,
  LogOut,
  Paperclip,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { AdminEligibility, AdminEligibilityV2, AdminManualPaymentsReview, ManualPayments, tokenStorage } from "@/lib/api";
import { LetterEnginePanel } from "@/components/LetterEnginePanel";
import { CreditReviewPanel } from "@/components/CreditReviewPanel";
import { DocumentPackPanel } from "@/components/DocumentPackPanel";
import { BureauPanel } from "@/components/BureauPanel";
import { toast } from "sonner";

const STATUS_LABEL = {
  pending: { text: "Awaiting payment", cls: "bg-white/10 text-white/70 border-white/20" },
  pending_verification: {
    text: "Proof under review",
    cls: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  },
  paid: { text: "Paid · Verified", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" },
};

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Phase 2a · Read-only link into V2 Preview. Hidden entirely when the
// backend reports preview_allow=false (i.e. production).
function V2PreviewLink({ checkId }) {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    AdminEligibilityV2.flags()
      .then((f) => { if (!cancelled) setAllowed(Boolean(f?.preview_allow)); })
      .catch(() => { if (!cancelled) setAllowed(false); });
    return () => { cancelled = true; };
  }, []);
  if (!allowed) return null;
  return (
    <Link
      to={`/admin/eligibility-v2/preview/${checkId}`}
      data-testid={`ec-v2-preview-link-${checkId}`}
      className="inline-flex items-center gap-1 mt-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200 hover:bg-amber-500/20"
      title="Open V2 deterministic preview (read-only)"
    >
      V2 Preview
    </Link>
  );
}

export default function AdminEligibilityPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);
  const [approving, setApproving] = useState(null); // check being confirmed
  const [approveBusy, setApproveBusy] = useState(false);
  const [deleting, setDeleting] = useState(null); // check pending soft/perm delete

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status === "trash") {
        params.only_deleted = true;
      } else if (status !== "all") {
        params.status = status;
      }
      const r = await AdminEligibility.list(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      if (e?.response?.status === 401) {
        tokenStorage.clear();
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [status, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/95 backdrop-blur border-b border-[#D4AF37]/25">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/leads"
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#F0D788]"
            >
              <ArrowLeft size={12} /> Back to Admin
            </Link>
            <div className="w-px h-6 bg-white/10 mx-2" />
            <h1 className="font-display text-base md:text-lg flex items-center gap-2">
              <ClipboardCheck size={16} className="text-[#F0D788]" /> Eligibility Checks
            </h1>
          </div>
          <Link
            to="/admin/letters-issued"
            data-testid="ec-nav-letters-issued"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37] bg-[#D4AF37]/[0.08] text-xs text-[#F0D788]"
          >
            <FileText size={12} /> Letters Issued
          </Link>
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-rose-400/40 text-xs text-white/80"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Metric label="In this view" value={total} testId="ec-total" />
          <Metric
            label="Paid · Verified"
            value={items.filter((i) => i.payment_status === "paid").length}
            accent="text-emerald-300"
            testId="ec-paid"
          />
          <Metric
            label="Proof under review"
            value={items.filter((i) => i.payment_status === "pending_verification").length}
            accent="text-amber-200"
            testId="ec-pending-verif"
          />
          <Metric
            label="Awaiting payment"
            value={items.filter((i) => i.payment_status === "pending").length}
            accent="text-white/60"
            testId="ec-awaiting"
          />
        </section>

        <div className="flex items-center gap-2 mb-4">
          {[
            ["all", "All"],
            ["pending_verification", "Proof under review"],
            ["paid", "Paid · Verified"],
            ["pending", "Awaiting payment"],
            ["trash", "Trash"],
          ].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setStatus(v)}
              data-testid={`ec-tab-${v}`}
              className={`px-3.5 py-1.5 rounded-full text-xs border transition ${
                status === v
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F0D788]"
                  : "border-white/10 text-white/60 hover:border-white/25"
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            data-testid="ec-refresh"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-white/70"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <section
          data-testid="ec-table"
          className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/55">
                <tr>
                  <th className="text-left px-4 py-3">Applicant</th>
                  <th className="text-left px-4 py-3">Loan · Income</th>
                  <th className="text-left px-4 py-3">Reference IDs</th>
                  <th className="text-left px-4 py-3">Payment</th>
                  <th className="text-left px-4 py-3">Documents</th>
                  <th className="text-left px-4 py-3">Verdict</th>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-white/55">
                      <Loader2 className="inline animate-spin mr-2" size={13} /> Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-white/60">
                      {status === "trash"
                        ? "Trash is empty."
                        : "No eligibility checks match this filter yet."}
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((c, i) => {
                    const st = STATUS_LABEL[c.payment_status] || STATUS_LABEL.pending;
                    const verdict = c.result || null;
                    const mp = c.manual_payment;
                    return (
                      <tr key={c.id} data-testid={`ec-row-${i}`} className="border-t border-white/5">
                        <td className="px-4 py-3">
                          <div className="text-white font-medium">{c.name}</div>
                          <div className="text-[11px] text-white/50">{c.email}</div>
                          <div className="text-[11px] text-white/40">{c.phone}</div>
                          {c.deleted_at && (
                            <div
                              data-testid={`ec-deleted-badge-${i}`}
                              className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border border-rose-400/40 bg-rose-500/10 text-rose-200 text-[10px] uppercase tracking-wider"
                              title={`Deleted ${c.deleted_at?.slice(0,16).replace('T',' ')} by ${c.deleted_by || 'admin'}`}
                            >
                              <Trash2 size={9} /> Deleted
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-white/80">
                          <div className="text-white/90">{c.loan_type_label || c.loan_type}</div>
                          <div className="text-[11px] text-white/50">
                            ₹{inr(c.monthly_income || c.annual_turnover)}
                            {c.employment_type && (
                              <span className="text-white/40"> · {c.employment_type}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[11px] text-white/70 font-mono leading-relaxed">
                          <div>
                            <span className="text-white/40">Check:</span> {c.id?.slice(0, 8)}
                          </div>
                          {mp?.id && (
                            <div>
                              <span className="text-white/40">Pay ref:</span> {mp.id.slice(0, 8)}
                            </div>
                          )}
                          {mp?.upi_reference && (
                            <div className="text-white/40">UPI: {mp.upi_reference}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${st.cls}`}
                          >
                            {st.text}
                          </span>
                          <div className="text-[11px] text-white/50 mt-1">
                            ₹{inr(mp?.amount || 1500)} · {mp ? "UPI" : c.payment_status === "paid" ? "Stripe" : "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(c.documents || []).length > 0 ? (
                            <button
                              data-testid={`ec-docs-open-${i}`}
                              onClick={() => setDetail(c)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37]/80 bg-[#D4AF37]/5 text-[11px] text-[#F0D788]"
                              title="Open the details drawer to view/download uploaded documents"
                            >
                              <Paperclip size={11} /> {c.documents.length} doc{c.documents.length > 1 ? "s" : ""}
                            </button>
                          ) : (
                            <span
                              data-testid={`ec-docs-none-${i}`}
                              className="text-[11px] text-white/40"
                            >
                              No docs
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-white/80">
                          {verdict ? (
                            <>
                              <div
                                className={`text-[11px] uppercase tracking-wider ${
                                  verdict.eligible ? "text-emerald-300" : "text-rose-300"
                                }`}
                              >
                                {verdict.eligible ? "Eligible" : "Not eligible"}
                              </div>
                              <div className="text-[#F0D788] font-semibold tabular-nums">
                                ₹{inr(verdict.eligible_amount || 0)}
                              </div>
                            </>
                          ) : (
                            <span className="text-white/40 text-[11px]">Pending assessment</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[11px] text-white/55">
                          {c.created_at?.slice(0, 16).replace("T", " ")}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {c.payment_status === "pending_verification" && mp && !c.deleted_at && (
                            <button
                              onClick={() => setApproving({ check: c, payment: mp })}
                              data-testid={`ec-approve-${i}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-400/50 hover:border-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 text-[11px] text-emerald-200 mr-1.5"
                            >
                              <CheckCircle2 size={11} /> Approve
                            </button>
                          )}
                          <button
                            onClick={() => setDetail(c)}
                            data-testid={`ec-view-${i}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#D4AF37]/35 hover:border-[#D4AF37]/70 text-[11px] text-[#F0D788] mr-1.5"
                          >
                            <Eye size={11} /> Details
                          </button>
                          {mp && (
                            <a
                              href={ManualPayments.proofUrl(mp.id)}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`ec-proof-${i}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-400/35 hover:border-emerald-400/70 text-[11px] text-emerald-300 mr-1.5"
                            >
                              <FileText size={11} /> Proof
                            </a>
                          )}
                          {c.deleted_at ? (
                            <>
                              <button
                                data-testid={`ec-restore-${i}`}
                                onClick={async () => {
                                  try {
                                    await AdminEligibility.restore(c.id);
                                    await load();
                                  } catch (e) {
                                    alert(e?.response?.data?.detail || "Restore failed");
                                  }
                                }}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-sky-400/40 hover:border-sky-400/70 text-[11px] text-sky-200 mr-1.5"
                              >
                                <RotateCcw size={11} /> Restore
                              </button>
                              <button
                                data-testid={`ec-perm-delete-${i}`}
                                onClick={() => setDeleting({ check: c, mode: "permanent" })}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose-400/50 hover:border-rose-400 bg-rose-500/10 text-[11px] text-rose-200"
                              >
                                <Trash2 size={11} /> Delete forever
                              </button>
                            </>
                          ) : (
                            <button
                              data-testid={`ec-delete-${i}`}
                              onClick={() => setDeleting({ check: c, mode: "soft" })}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose-400/40 hover:border-rose-400/70 text-[11px] text-rose-300"
                            >
                              <Trash2 size={11} /> Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {detail && <DetailDrawer check={detail} onClose={() => setDetail(null)} />}
      {approving && (
        <ApproveDialog
          check={approving.check}
          payment={approving.payment}
          busy={approveBusy}
          onClose={() => setApproving(null)}
          onConfirm={async (note) => {
            setApproveBusy(true);
            try {
              await AdminManualPaymentsReview.review(approving.payment.id, "approve", note);
              setApproving(null);
              await load();
            } catch (e) {
              alert(e?.response?.data?.detail || "Approval failed");
            } finally {
              setApproveBusy(false);
            }
          }}
        />
      )}
      {deleting && (
        <DeleteDialog
          check={deleting.check}
          mode={deleting.mode}
          onClose={() => setDeleting(null)}
          onConfirmed={async () => {
            setDeleting(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function Metric({ label, value, accent = "text-white", testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</div>
      <div className={`mt-1.5 text-2xl font-display tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

function DetailDrawer({ check, onClose }) {
  const mp = check.manual_payment;
  const docs = check.documents || [];
  const verdict = check.result || null;
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      data-testid="ec-detail-drawer"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0A1F44] border-l border-[#D4AF37]/30 overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
              Eligibility Check
            </div>
            <div className="font-display text-lg mt-1 text-white">{check.name}</div>
            <div className="text-[11px] text-white/55 font-mono">{check.id}</div>
            <V2PreviewLink checkId={check.id} />
          </div>
          <button
            onClick={onClose}
            data-testid="ec-detail-close"
            className="text-white/60 hover:text-white transition p-1"
            aria-label="Close details"
          >
            <X size={18} />
          </button>
        </div>

        <Section title="Contact">
          <Row label="Mobile" value={check.phone} />
          <Row label="Email" value={check.email} />
        </Section>

        <Section title="Loan Details">
          <Row label="Loan Type" value={check.loan_type_label || check.loan_type} />
          <Row label="Monthly Income" value={`₹${inr(check.monthly_income)}`} />
          <Row label="Annual Turnover" value={`₹${inr(check.annual_turnover)}`} />
          <Row label="Employment" value={check.employment_type || "—"} />
        </Section>

        <Section title="Payment">
          <Row
            label="Status"
            value={
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${
                  (STATUS_LABEL[check.payment_status] || STATUS_LABEL.pending).cls
                }`}
              >
                {(STATUS_LABEL[check.payment_status] || STATUS_LABEL.pending).text}
              </span>
            }
          />
          <Row label="Amount" value={`₹${inr(mp?.amount || 1500)}`} />
          <Row label="Method" value={mp ? "UPI / Bank Transfer" : "Stripe (if paid)"} />
          {mp && (
            <>
              <Row label="Payment ID" value={mp.id} mono />
              <Row label="UPI Reference" value={mp.upi_reference || "—"} mono />
              <Row
                label="Screenshot"
                value={
                  <a
                    href={ManualPayments.proofUrl(mp.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#F0D788] underline hover:text-white"
                  >
                    Open in new tab →
                  </a>
                }
              />
            </>
          )}
        </Section>

        {verdict && (
          <Section title="Assessment Verdict">
            <Row
              label="Result"
              value={
                <span
                  className={`text-[10px] uppercase tracking-wider ${
                    verdict.eligible ? "text-emerald-300" : "text-rose-300"
                  }`}
                >
                  {verdict.eligible ? "Eligible" : "Not eligible"}
                </span>
              }
            />
            <Row label="Amount" value={`₹${inr(verdict.eligible_amount || 0)}`} />
            {verdict.deposit_reason && <Row label="Deposit" value={verdict.deposit_reason} />}
            {verdict.reason && <Row label="Notes" value={verdict.reason} />}
          </Section>
        )}

        <Section title="Credit Bureau">
          <BureauPanel check={check} />
        </Section>
        {docs.length > 0 && (
          <Section title={`Documents (${docs.length})`}>
            <div className="pb-3 px-3 flex flex-wrap gap-2">
              <a
                data-testid={`ec-doc-pack-${check.id}`}
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/eligibility/${check.id}/document-pack.pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37] text-[#0A1F44] hover:brightness-110 text-[11px] font-semibold shadow-lg shadow-[#D4AF37]/20"
              >
                <Download size={11} /> Download Eligibility Pack (all docs · T&amp;C · confirmation · watermarked)
              </a>
            </div>
            <DocumentPackPanel check={check} />
            {docs.map((d) => (
              <div
                key={d.id}
                data-testid={`ec-doc-${d.id}`}
                className="text-[11px] text-white/70 py-2 px-3 flex items-center gap-2"
              >
                <ShieldCheck size={11} className="text-[#D4AF37] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white/90 font-medium">{d.label}</div>
                  <div className="text-white/40 truncate">{d.filename}</div>
                </div>
                <button
                  data-testid={`ec-doc-view-${d.id}`}
                  onClick={async () => {
                    try {
                      const url = await AdminEligibility.documentBlobUrl(check.id, d.id);
                      window.open(url, "_blank", "noopener,noreferrer");
                    } catch (e) {
                      alert(e?.response?.data?.detail || "Could not open document");
                    }
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37]/80 text-[10px] text-[#F0D788]"
                >
                  <ExternalLink size={10} /> View
                </button>
              </div>
            ))}
          </Section>
        )}

        {/* AI Analysis section — supplemental only */}
        <AiAnalysisPanel check={check} />
      </div>
    </div>
  );
}


/**
 * Shows every top-priority field the AI extracted from documents alongside a
 * confidence chip (High / Medium / Low). Helps admins spot values that need
 * manual verification before sending the letter to the customer.
 * Confidence data is written by Claude into `payload.field_confidence`.
 */
function ExtractionPreview({ report }) {
  const payload = report?.payload || {};
  const fc = payload.field_confidence || {};
  const profile = payload.applicant_profile || {};
  const rec = payload.eligibility_recommendation || {};
  const snap = payload.credit_score_snapshot || {};

  // Pick the highest-signal fields to preview — the ones an admin usually
  // wants to double-check before the letter goes to a real customer.
  const rows = [
    ["PAN", profile.pan, fc["applicant_profile.pan"]],
    ["Full Name", profile.full_name, fc["applicant_profile.full_name"]],
    ["Date of Birth", profile.date_of_birth, fc["applicant_profile.date_of_birth"]],
    ["Aadhaar (last 4)", profile.aadhaar_last4, fc["applicant_profile.aadhaar_last4"]],
    ["Business Name", profile.business_name, fc["applicant_profile.business_name"]],
    ["GSTIN", profile.gstin, fc["applicant_profile.gstin"]],
    ["Udyam No.", profile.udyam_number, fc["applicant_profile.udyam_number"]],
    ["Vintage", profile.vintage_years, fc["applicant_profile.vintage_years"]],
    ["Bureau Score", snap.score, fc["credit_score_snapshot.score"]],
    [
      "Recommended Limit (Lower)",
      rec.recommended_limit_lower_inr,
      fc["eligibility_recommendation.recommended_limit_lower_inr"],
    ],
    [
      "Recommended Limit (Upper)",
      rec.recommended_limit_upper_inr,
      fc["eligibility_recommendation.recommended_limit_upper_inr"],
    ],
  ].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "");

  if (rows.length === 0 || Object.keys(fc).length === 0) return null;

  return (
    <div
      data-testid="ec-ai-extraction-preview"
      className="mb-3 rounded-lg border border-white/12 bg-white/[0.03] p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">
          Extracted Fields &middot; AI Confidence
        </div>
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-white/40">
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400"/>High</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400"/>Medium</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-400"/>Low</span>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-1.5">
        {rows.map(([label, value, conf]) => (
          <div
            key={label}
            data-testid={`ec-conf-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="flex items-start justify-between gap-2 rounded-md bg-black/25 border border-white/5 px-2 py-1.5"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-wider text-white/45">{label}</div>
              <div className="text-[11px] text-white/90 truncate mt-0.5">{value}</div>
            </div>
            <ConfChip level={conf} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfChip({ level }) {
  const lvl = (level || "low").toString().toLowerCase();
  const styles = {
    high: "border-emerald-400/50 bg-emerald-500/15 text-emerald-200",
    medium: "border-amber-400/50 bg-amber-500/15 text-amber-200",
    low: "border-rose-400/50 bg-rose-500/15 text-rose-200",
  };
  const label = lvl === "high" ? "HIGH" : lvl === "medium" ? "MED" : "LOW";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[9px] font-semibold tracking-wider shrink-0 ${
        styles[lvl] || styles.low
      }`}
    >
      {label}
    </span>
  );
}


function AiAnalysisPanel({ check }) {
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [decision, setDecision] = useState(false); // approve/reject in progress
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const report = check.ai_report;
  const paid = check.payment_status === "paid";
  const isFallback = report?.ai_source === "fallback";
  const letterStatus = report?.letter_status; // ready_to_send | fallback_draft_review_required | fallback_rejected
  const needsReview = letterStatus === "fallback_draft_review_required";
  const wasRejected = letterStatus === "fallback_rejected";

  // Parse both plain-string and structured bilingual error payloads.
  const extractErr = (e) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === "object" && (d.message_en || d.message_gu)) {
      return {
        en: d.message_en || "",
        gu: d.message_gu || "",
        code: d.code || "",
        retryable: !!d.retryable,
      };
    }
    return { en: typeof d === "string" ? d : "Letter generation failed", gu: "", code: "", retryable: false };
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      try {
        await AdminEligibility.generateLetter(check.id, undefined, { onProgress: (j) => setProgress(j.status === "running" ? "Rendering letter… (background job, safe to wait)" : "Queued…") });
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 409 && d?.code === "REVIEW_CONFIRMATION_REQUIRED") {
          if (!window.confirm(`Overridden fields: ${(d.overridden_fields || []).join(", ")}\n\nGenerate Final Eligibility Letter Using These Reviewed Values?`)) throw e;
          await AdminEligibility.generateLetterConfirmed(check.id, undefined);
        } else throw e;
      }
      setProgress(null);
      const url = await AdminEligibility.aiPdfBlobUrl(check.id);
      setPdfBlobUrl(url);
      setShowViewer(true);
      window.dispatchEvent(new CustomEvent("ec-ai-report-updated", { detail: { id: check.id } }));
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async () => {
    setBusy(true);
    setError(null);
    try {
      const url = await AdminEligibility.aiPdfBlobUrl(check.id);
      setPdfBlobUrl(url);
      setShowViewer(true);
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const url = await AdminEligibility.aiPdfBlobUrl(check.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sahyog-eligibility-letter-${check.id.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
    } catch (e) {
      setError(extractErr(e));
    }
  };

  const sendToCustomer = async () => {
    if (!window.confirm(`Send this letter to ${check.email}?`)) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await AdminEligibility.sendLetter(check.id);
      setSuccess(`Letter emailed to ${check.email} · Ref ${r.reference_no}`);
      toast.success(`Letter v${r.version_no ?? ""} sent`, { description: `${r.reference_no} → ${check.email}` });
      window.dispatchEvent(new CustomEvent("ec-ai-report-updated", { detail: { id: check.id } }));
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "SUPERSEDE_CONFIRMATION_REQUIRED") {
        toast("Send cancelled", { description: "The newer version was not sent. Nothing was recorded." });
      } else {
        setError(extractErr(e));
        toast.error("Letter not sent", { description: extractErr(e) });
      }
    } finally {
      setSending(false);
    }
  };

  const approveDraft = async () => {
    const note = window.prompt("Optional approval note (audit log)") || "";
    setDecision(true);
    setError(null);
    setSuccess(null);
    try {
      await AdminEligibility.approveFallbackLetter(check.id, note);
      setSuccess("Draft approved. You can now email it to the applicant.");
      window.dispatchEvent(new CustomEvent("ec-ai-report-updated", { detail: { id: check.id } }));
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setDecision(false);
    }
  };

  const rejectDraft = async () => {
    const note = window.prompt("Reason for rejecting this draft (recorded in audit log)") || "";
    if (!note.trim() && !window.confirm("Reject without a note?")) return;
    setDecision(true);
    setError(null);
    setSuccess(null);
    try {
      await AdminEligibility.rejectFallbackLetter(check.id, note);
      setSuccess("Draft rejected. Regenerate the letter when ready.");
      window.dispatchEvent(new CustomEvent("ec-ai-report-updated", { detail: { id: check.id } }));
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setDecision(false);
    }
  };

  const sentAt = report?.sent_to_customer_at;

  return (
    <div className="mb-6" data-testid="ec-ai-panel">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2 flex items-center gap-1.5">
        <FileText size={11} /> Preliminary Loan Eligibility Letter
      </div>
      <div className="rounded-xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#1a1f3a] to-[#0d1428] p-4">
        {!report && (
          <>
            <p className="text-xs text-white/70 leading-relaxed mb-3">
              Generate the <strong>Preliminary Loan Eligibility &amp; Credit Assessment</strong> (Master v1.0):
              the engine analyses this applicant's submission and documents, then renders the
              locked 6-page PDF — snapshot &amp; reconciliation, bank/income analysis, credit &amp;
              liability review, underwriting recommendation (computer-generated, no signature),
              Attachment Letter A (30% security deposit) and Letter B (4.50% processing consent).
            </p>
            <button
              data-testid="ec-ai-run"
              onClick={generate}
              disabled={busy || !paid}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#F0D788] text-xs font-semibold disabled:opacity-40"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {busy ? "Generating…" : "Generate Eligibility Letter"}
            </button>
            {!paid && (
              <div className="mt-2 text-[10px] text-amber-300">
                Approve the eligibility payment first — the letter is issued only for
                paid applications.
              </div>
            )}
          </>
        )}
        {report && (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-2.5 py-1.5">
                <div className="text-[9px] uppercase tracking-wider text-white/50">Reference</div>
                <div className="text-[11px] font-mono text-[#F0D788] mt-0.5 break-all">
                  {report.reference_no || "—"}
                </div>
              </div>
              <div className="rounded-lg border border-white/15 px-2.5 py-1.5">
                <div className="text-[9px] uppercase tracking-wider text-white/50">Sent to Customer</div>
                <div className="text-[11px] mt-0.5 font-semibold">
                  {sentAt ? (
                    <span className="text-emerald-300">
                      {new Date(sentAt).toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-white/50">Not yet</span>
                  )}
                </div>
              </div>
            </div>
            <ExtractionPreview report={report} />
            <div className="text-[10px] text-white/40 mb-3">
              Generated {new Date(report.generated_at).toLocaleString()} · {report.model}
              {report.generated_by ? ` · by ${report.generated_by}` : ""}
            </div>
            {Array.isArray(report.docs_attached_to_llm) && report.docs_attached_to_llm.length > 0 && (
              <div
                data-testid="ec-ai-vision-audit"
                className="mb-3 rounded-lg border border-emerald-400/25 bg-emerald-500/[0.06] px-2.5 py-2"
              >
                <div className="text-[9px] uppercase tracking-wider text-emerald-300/85 flex items-center gap-1 mb-1">
                  <Eye size={9} /> Documents read by AI
                </div>
                <ul className="text-[10px] text-white/75 leading-tight space-y-0.5">
                  {report.docs_attached_to_llm.map((s, i) => (
                    <li key={`doc-${i}-${String(s).slice(0, 32)}`}>· {s}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="ec-ai-view"
                onClick={openPdf}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[11px] text-[#F0D788] disabled:opacity-40"
              >
                <Eye size={11} /> Preview PDF
              </button>
              <button
                data-testid="ec-ai-download"
                onClick={downloadPdf}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400/40 hover:border-emerald-400/80 text-[11px] text-emerald-200"
              >
                <Download size={11} /> Download PDF
              </button>
              {needsReview ? (
                <>
                  <button
                    data-testid="ec-ai-approve-draft"
                    onClick={approveDraft}
                    disabled={decision}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400/50 hover:border-emerald-400 bg-emerald-500/10 text-[11px] text-emerald-200 disabled:opacity-40"
                  >
                    {decision ? <Loader2 size={11} className="animate-spin" /> : null}
                    Approve &amp; Email
                  </button>
                  <button
                    data-testid="ec-ai-reject-draft"
                    onClick={rejectDraft}
                    disabled={decision}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-400/50 hover:border-rose-400 bg-rose-500/10 text-[11px] text-rose-200 disabled:opacity-40"
                  >
                    Reject Draft
                  </button>
                </>
              ) : (
                <button
                  data-testid="ec-ai-send"
                  onClick={sendToCustomer}
                  disabled={sending || !check.email || wasRejected}
                  title={!check.email ? "Applicant has no email on file" : wasRejected ? "Draft was rejected — regenerate to send" : undefined}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-sky-400/50 hover:border-sky-400 bg-sky-500/10 text-[11px] text-sky-200 disabled:opacity-40"
                >
                  {sending ? <Loader2 size={11} className="animate-spin" /> : <ExternalLink size={11} />}
                  {sentAt ? "Re-send to Customer" : "Send to Customer"}
                </button>
              )}
              <button
                data-testid="ec-ai-rerun"
                onClick={generate}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-white/40 text-[11px] text-white/70 disabled:opacity-40"
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                {needsReview || wasRejected ? "Regenerate with AI" : "Regenerate"}
              </button>
            </div>
          </>
        )}
        {success && (
          <div className="mt-3 text-[11px] text-emerald-200 bg-emerald-500/10 border border-emerald-500/25 rounded px-3 py-2">
            {success}
          </div>
        )}
        {progress && <div data-testid="letter-job-progress" className="text-[11px] text-[#F0D788] mb-2">{progress}</div>}
            {error && (
          <div
            data-testid="ec-ai-error"
            className="mt-3 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded px-3 py-2 space-y-1"
          >
            {typeof error === "object" ? (
              <>
                {error.en && <div><span className="opacity-60 uppercase tracking-wider text-[9px] mr-1">EN</span>{error.en}</div>}
                {error.gu && <div className="text-rose-200"><span className="opacity-60 uppercase tracking-wider text-[9px] mr-1">GU</span>{error.gu}</div>}
                {error.code && <div className="opacity-40 text-[9px]">code: {error.code}{error.retryable ? " · retryable" : ""}</div>}
              </>
            ) : (
              <div>{String(error)}</div>
            )}
          </div>
        )}
        {report && needsReview && (
          <div
            data-testid="ec-ai-fallback-banner"
            className="mt-3 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2"
          >
            <div className="font-semibold flex items-center gap-1.5 mb-0.5">
              Fallback Draft — Admin Review Required
            </div>
            <div className="text-amber-200/80 text-[10.5px] leading-relaxed">
              This draft was generated without AI enrichment. Review the PDF, then
              <strong> Approve &amp; Email</strong>, <strong>Regenerate with AI</strong>, or
              <strong> Reject Draft</strong>. The applicant will not receive an email until you approve.
            </div>
          </div>
        )}
        {report && wasRejected && (
          <div
            data-testid="ec-ai-rejected-banner"
            className="mt-3 text-[11px] text-rose-200 bg-rose-500/10 border border-rose-500/25 rounded px-3 py-2"
          >
            Draft rejected. Click <strong>Regenerate with AI</strong> to try again.
          </div>
        )}
        {paid && (
          <>
            <CreditReviewPanel check={check} />
            <LetterEnginePanel
            check={check}
            hasLetter={!!report}
            onRegenerated={() =>
              window.dispatchEvent(new CustomEvent("ec-ai-report-updated", { detail: { id: check.id } }))
            }
          />
          </>
        )}
      </div>
      {showViewer && pdfBlobUrl && (
        <PdfViewer url={pdfBlobUrl} onClose={() => setShowViewer(false)} />
      )}
    </div>
  );
}

function RiskChip({ label, value, neutral = false }) {
  const v = String(value || "").toLowerCase();
  const cls = neutral
    ? "border-white/15 text-white/85"
    : v === "high"
      ? "border-rose-400/50 text-rose-200 bg-rose-500/10"
      : v === "low"
        ? "border-emerald-400/50 text-emerald-200 bg-emerald-500/10"
        : "border-amber-400/50 text-amber-200 bg-amber-500/10";
  return (
    <div className={`rounded-lg border ${cls} px-2 py-1.5`}>
      <div className="text-[9px] uppercase tracking-wider text-white/50">{label}</div>
      <div className="text-[11px] font-semibold mt-0.5">
        {String(value || "—").toUpperCase()}
      </div>
    </div>
  );
}

function PdfViewer({ url, onClose }) {
  return (
    <div
      data-testid="ec-pdf-viewer"
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl h-full max-h-[92vh] bg-[#0A1F44] border border-[#D4AF37]/40 rounded-2xl overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
            AI Underwriting Report
          </div>
          <div className="flex items-center gap-2">
            <a
              href={url}
              download="sahyog-ai-report.pdf"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400/40 text-[11px] text-emerald-200"
            >
              <Download size={11} /> Download
            </a>
            <button
              data-testid="ec-pdf-viewer-close"
              onClick={onClose}
              className="text-white/60 hover:text-white p-1"
              aria-label="Close PDF viewer"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <iframe
          title="AI Underwriting Report"
          src={url}
          className="flex-1 w-full bg-white"
        />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{title}</div>
      <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2.5">
      <div className="text-[11px] uppercase tracking-wider text-white/50 pt-0.5">{label}</div>
      <div
        className={`text-xs text-right text-white/90 ${mono ? "font-mono break-all" : ""}`}
        style={{ maxWidth: "60%" }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function ApproveDialog({ check, payment, busy, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  return (
    <div
      data-testid="ec-approve-dialog"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-emerald-400/40 bg-[#0d1a12] p-6"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-emerald-500/15 text-emerald-300">
            <CheckCircle2 size={18} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg text-white">Approve Eligibility Check?</div>
            <div className="text-xs text-white/60 mt-1 leading-relaxed">
              This will mark the payment as verified, run the eligibility assessment, and
              automatically send the customer an <strong>Email</strong> and{" "}
              <strong>SMS</strong> with their approval + next steps.
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="rounded-lg bg-white/[0.04] border border-white/10 p-3 mb-4 text-sm">
          <div className="text-white font-medium">{check.name}</div>
          <div className="text-[11px] text-white/60">
            {check.loan_type_label || check.loan_type} · ₹{Number(payment.amount || 1500).toLocaleString("en-IN")}
          </div>
          <div className="text-[10px] text-white/40 font-mono mt-1">
            REF-{check.id?.slice(0, 8).toUpperCase()} · Pay {payment.id?.slice(0, 8)}
          </div>
          <div className="text-[10px] text-white/50 mt-1.5">
            Email {check.email} · Mobile {check.phone}
          </div>
        </div>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-white/55 mb-2">
          Internal note (optional)
        </label>
        <textarea
          data-testid="ec-approve-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Verified UPI proof · TXN123 · matches on bank statement"
          className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-400/60"
        />

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-xs border border-white/15 text-white/70 hover:border-white/40"
          >
            Cancel
          </button>
          <button
            data-testid="ec-approve-submit"
            disabled={busy}
            onClick={() => onConfirm(note.trim())}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-40 transition"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            Confirm · Send Email + SMS
          </button>
        </div>
      </div>
    </div>
  );
}




function DeleteDialog({ check, mode, onClose, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const expected = `DELETE ${check.name || ""}`;
  const isPermanent = mode === "permanent";

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isPermanent) {
        await AdminEligibility.permanentDelete(check.id, confirmText.trim());
      } else {
        await AdminEligibility.softDelete(check.id);
      }
      onConfirmed?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="ec-delete-dialog"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-rose-400/40 bg-[#1a0d0d] p-6"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-rose-500/15 text-rose-300">
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg text-white">
              {isPermanent ? "Delete this check permanently?" : "Move this check to Trash?"}
            </div>
            <div className="text-xs text-white/60 mt-1 leading-relaxed">
              {isPermanent ? (
                <>
                  This is <strong className="text-rose-300">irreversible</strong>. The
                  eligibility check row, uploaded documents, and any generated AI report
                  will be removed. The linked payment record (if any) is preserved for
                  financial audit.
                </>
              ) : (
                <>
                  The check is hidden from the primary view but can be restored from the{" "}
                  <strong>Trash</strong> tab at any time. No documents are deleted.
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="rounded-lg bg-white/[0.04] border border-white/10 p-3 mb-4 text-sm">
          <div className="text-white font-medium">{check.name}</div>
          <div className="text-[11px] text-white/60">
            {check.loan_type_label || check.loan_type}
          </div>
          <div className="text-[10px] text-white/40 font-mono mt-1">
            REF-{check.id?.slice(0, 8).toUpperCase()}
          </div>
          <div className="text-[10px] text-white/50 mt-1.5">
            Docs on file: {(check.documents || []).length}
          </div>
        </div>

        {isPermanent && (
          <>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/55 mb-2">
              Type <span className="text-rose-300 font-mono">{expected}</span> to confirm
            </label>
            <input
              data-testid="ec-delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expected}
              autoFocus
              className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-rose-400/60 font-mono"
            />
          </>
        )}

        {error && (
          <div className="mt-3 text-[11px] text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-xs border border-white/15 text-white/70 hover:border-white/40"
          >
            Cancel
          </button>
          <button
            data-testid="ec-delete-submit"
            disabled={busy || (isPermanent && confirmText.trim() !== expected)}
            onClick={run}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-rose-500 hover:bg-rose-400 text-white disabled:opacity-40 transition"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {isPermanent ? "Delete forever" : "Move to Trash"}
          </button>
        </div>
      </div>
    </div>
  );
}
